<template>
  <div class="h-full overflow-scroll p-4 fade-in max-w-screen-xl w-full mx-auto">
    <div class="w-full max-w-screen-2xl mx-auto flex flex-col">
      <router-link
        to="/manage?tab=pod"
        class="text-xs text-gray-500 hover:text-gray-700 flex items-center mb-2 rounded-lg hover:bg-gray-200 w-fit py-2 px-1 transition-all duration-200"
      >
        <ArrowLeft class="w-4 h-4 mr-2" />
        Back to Pods
      </router-link>
      <div class="flex items-center mb-4">
        <BadgePlus class="w-5 h-5 mr-2" />
        <h1 class="text-xl font-normal cursor-default">Create New Pod</h1>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <!-- Left Column: Pod Form -->
        <div class="bg-white p-4 px-6 rounded-xl border-[1px] border-gray-200">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Pod Name</label>
              <input
                v-model="form.name"
                type="text"
                placeholder="e.g., Pod Alpha"
                class="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-cosmicPurple/20 focus:border-cosmicPurple"
              />
              <p v-if="errors.name" class="text-xs text-red-500 mt-1">{{ errors.name }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                v-model="form.start_date"
                type="date"
                class="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-cosmicPurple/20 focus:border-cosmicPurple"
              />
              <p v-if="errors.start_date" class="text-xs text-red-500 mt-1">{{ errors.start_date }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">First Meeting Date (optional)</label>
              <input
                v-model="form.first_meeting_date"
                type="date"
                class="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-cosmicPurple/20 focus:border-cosmicPurple"
              />
              <p class="text-xs text-gray-400 mt-1">Used in the welcome message posted to the Slack channel</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Details (optional)</label>
              <textarea
                v-model="form.details"
                placeholder="Brief description of the pod"
                rows="3"
                class="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-2 focus:ring-cosmicPurple/20 focus:border-cosmicPurple"
              />
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-4">
          <div class="bg-white p-4 px-6 rounded-xl border-[1px] border-gray-200 space-y-4">
            <div class="flex items-center gap-2">
              <h2 class="text-lg">Add Fellows</h2>
            </div>
            <UserSelection v-model="selectedFellows" />
          </div>

          <div class="bg-white p-4 px-6 rounded-xl border-[1px] border-gray-200">
            <div class="flex items-center gap-2">
              <h2 class="text-lg">Add Guides</h2>
            </div>
            <p class="text-sm text-gray-500 mb-4">
              You will be added as a guide automatically since you are the creator
            </p>
            <GuideSelection v-model="selectedGuides" />
          </div>

          <div class="bg-white p-4 px-6 rounded-xl border-[1px] border-gray-200">
            <div class="flex items-center gap-2">
              <h2 class="text-lg">Choose Main Guide</h2>
            </div>
            <p class="text-sm text-gray-500 mb-4">
              Select a main guide for this pod
            </p>
            <MainGuideSelection v-model="selectedMainGuideId" :guides="selectedGuides" />
          </div>

          <div class="bg-white p-4 px-6 rounded-xl border-[1px] border-gray-200">
            <div class="flex items-center gap-2">
              <h2 class="text-lg">Choose Resume Mentor</h2>
            </div>
            <p class="text-sm text-gray-500 mb-4">
              Optional — select a resume mentor for this pod
            </p>
            <MainGuideSelection
              v-model="selectedResumeMentorId"
              :guides="selectedGuides"
              label="Resume mentor"
              placeholder="Select resume mentor"
            />
          </div>

          <div class="bg-white p-4 px-6 rounded-xl border-[1px] border-gray-200">
            <div class="flex items-center gap-2">
              <h2 class="text-lg">Choose Behavioral Mentor</h2>
            </div>
            <p class="text-sm text-gray-500 mb-4">
              Optional — select a behavioral mentor for this pod
            </p>
            <MainGuideSelection
              v-model="selectedBehavioralMentorId"
              :guides="selectedGuides"
              label="Behavioral mentor"
              placeholder="Select behavioral mentor"
            />
          </div>
        </div>
      </div>
      <button
        @click="onSubmit"
        :disabled="isCreating"
        class="w-fit text-xs mt-2 ml-auto bg-cosmicPurple text-white p-2 px-4 rounded-md hover:bg-cosmicPurple/80 hover:-translate-y-[1px] transition-all duration-200 flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
      >
        Create Pod
      </button>
    </div>

    <!-- Progress overlay -->
    <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="isCreating"
        class="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      >
        <div class="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm mx-4">
          <h2 class="text-base font-semibold text-gray-900 mb-1">Setting up your pod</h2>
          <p class="text-xs text-gray-400 mb-6">This may take up to a minute. Please wait…</p>

          <ul class="space-y-3">
            <li
              v-for="step in steps"
              :key="step.id"
              class="flex items-center gap-3"
            >
              <!-- Icon -->
              <div
class="w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-colors duration-300"
                :class="{
                  'bg-green-100': step.state === 'done',
                  'bg-red-100': step.state === 'failed',
                  'bg-cosmicPurple/10': step.state === 'active',
                  'bg-gray-100': step.state === 'pending',
                }"
              >
                <Check v-if="step.state === 'done'" class="w-3.5 h-3.5 text-green-600" />
                <X v-else-if="step.state === 'failed'" class="w-3.5 h-3.5 text-red-500" />
                <Loader2 v-else-if="step.state === 'active'" class="w-3.5 h-3.5 text-cosmicPurple animate-spin" />
                <component v-else :is="step.icon" class="w-3.5 h-3.5 text-gray-300" />
              </div>

              <!-- Label -->
              <div class="min-w-0">
                <p
class="text-sm leading-tight transition-colors duration-300"
                  :class="{
                    'text-gray-800 font-medium': step.state === 'done',
                    'text-red-600 font-medium': step.state === 'failed',
                    'text-cosmicPurple font-medium': step.state === 'active',
                    'text-gray-300': step.state === 'pending',
                  }"
                >{{ step.label }}</p>
                <p
v-if="step.state === 'active' || step.state === 'failed'"
                  class="text-[11px] mt-0.5 truncate max-w-[220px]"
                  :class="step.state === 'failed' ? 'text-red-400' : 'text-gray-400'"
                  :title="step.detail"
                >{{ step.detail }}</p>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </Transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import { usePodsStore } from '@/stores/pods'
import { BadgePlus, ArrowLeft, Check, X, Loader2, Box, Hash, FolderOpen, Users, Mail } from 'lucide-vue-next'
import UserSelection from '@/components/manage/UserSelection.vue'
import GuideSelection from '@/components/manage/GuideSelection.vue'
import MainGuideSelection from '@/components/manage/MainGuideSelection.vue'
import { useRouter } from 'vue-router'
import type { User } from '@/types/user'
import { useToast } from 'primevue/usetoast'
import type { Component } from 'vue'

const podsStore = usePodsStore()
const router = useRouter()
const toast = useToast()

const form = reactive({
  name: '',
  start_date: '',
  first_meeting_date: '',
  details: ''
})

const errors = reactive<Record<string, string>>({})
const selectedFellows = ref<User[]>([])
const selectedGuides = ref<User[]>([])
const selectedMainGuideId = ref<string | null>(null)
const selectedResumeMentorId = ref<string | null>(null)
const selectedBehavioralMentorId = ref<string | null>(null)
const isCreating = ref(false)

type StepState = 'pending' | 'active' | 'done' | 'failed'
interface Step { id: string; provKey?: string; label: string; detail: string; icon: Component; state: StepState; delay: number }

const steps = reactive<Step[]>([
  { id: 'pod',    provKey: undefined,      label: 'Creating pod',                  detail: 'Saving pod details and assigning members…',      icon: Box,        state: 'pending', delay: 0 },
  { id: 'drive',  provKey: 'drive',        label: 'Creating Google Drive folders', detail: 'Setting up personal folders for each fellow…',    icon: FolderOpen, state: 'pending', delay: 2500 },
  { id: 'slack',  provKey: 'slack',        label: 'Setting up Slack channel',      detail: 'Creating channel and inviting members…',          icon: Hash,       state: 'pending', delay: 6000 },
  { id: 'group',  provKey: 'google_group', label: 'Creating Google Group',         detail: 'Setting up the group email…',                     icon: Users,      state: 'pending', delay: 10000 },
  { id: 'invite', provKey: 'invites',      label: 'Inviting fellows to group',     detail: 'Sending invitations to group members…',           icon: Mail,       state: 'pending', delay: 13000 },
])

let stepTimers: ReturnType<typeof setTimeout>[] = []

function startProgress() {
  steps.forEach((s) => (s.state = 'pending'))
  stepTimers = []

  steps.forEach((step, i) => {
    stepTimers.push(setTimeout(() => {
      step.state = 'active'
      if (i > 0) steps[i - 1].state = 'done'
    }, step.delay))
  })
}

function completeProgress(provisioning?: Record<string, { success: boolean; detail: string }>) {
  stepTimers.forEach(clearTimeout)
  stepTimers = []
  steps.forEach((s) => {
    if (s.provKey && provisioning) {
      const result = provisioning[s.provKey]
      s.state = result ? (result.success ? 'done' : 'failed') : 'done'
      if (result && !result.success) s.detail = result.detail
    } else {
      s.state = 'done'
    }
  })
}

function resetProgress() {
  stepTimers.forEach(clearTimeout)
  stepTimers = []
  steps.forEach((s) => (s.state = 'pending'))
}

function validate(): boolean {
  Object.keys(errors).forEach((k) => delete errors[k])
  if (!form.name?.trim()) { errors.name = 'Name is required'; return false }
  if (!form.start_date) { errors.start_date = 'Start date is required'; return false }
  return true
}

async function onSubmit() {
  if (!validate()) return

  const payload = {
    name: form.name.trim(),
    start_date: form.start_date,
    first_meeting_date: form.first_meeting_date || null,
    details: form.details?.trim() || undefined,
    fellow_ids: selectedFellows.value.map((u) => u.id),
    guide_ids: selectedGuides.value.map((u) => u.id),
    main_guide_id: selectedMainGuideId.value ? Number(selectedMainGuideId.value) : null,
    resume_mentor_id: selectedResumeMentorId.value ? Number(selectedResumeMentorId.value) : null,
    behavioral_mentor_id: selectedBehavioralMentorId.value
      ? Number(selectedBehavioralMentorId.value)
      : null,
  }

  isCreating.value = true
  startProgress()

  try {
    const result = await podsStore.createPod(payload)
    const provisioning = (result as any)?.provisioning
    completeProgress(provisioning)

    const anyFailed = provisioning && Object.values(provisioning).some((s: any) => !s.success)
    await new Promise((r) => setTimeout(r, anyFailed ? 2500 : 800))

    if (anyFailed) {
      const failed = Object.entries(provisioning)
        .filter(([, v]: any) => !v.success)
        .map(([k]: any) => k.replace('_', ' '))
        .join(', ')
      toast.add({ severity: 'warn', summary: 'Pod created with warnings', detail: `Some steps failed: ${failed}. Check the logs.`, life: 6000 })
    } else {
      toast.add({ severity: 'success', summary: 'Pod created!', detail: 'All steps completed successfully.', life: 3000 })
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    router.push('/manage?tab=pod')
  } catch {
    resetProgress()
    isCreating.value = false
    toast.add({ severity: 'error', summary: 'Error creating pod', detail: podsStore.error ?? 'An unknown error occurred.' })
  } finally {
    isCreating.value = false
  }
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
